import { Link } from "react-router-dom";
import { AlertCircle, Camera, ChevronRight, Image as ImageIcon } from "lucide-react";
import type { ComercialUnitRow } from "@/services/comercial";
import type { ChaoUnitMetrics } from "@/services/chaoDeObra";
import { formatDateTime } from "@/utils/format";
import { cn } from "@/utils/cn";
import { chaoUnitRoute, listUnitBadge } from "./chaoUnitCardUtils";

export interface ChaoUnitProgressCardProps {
  unit: ComercialUnitRow;
  metrics?: ChaoUnitMetrics;
  constructiveUnitLabel?: string;
  onOpen?: () => void;
}

export function ChaoUnitProgressCard({
  unit,
  metrics,
  constructiveUnitLabel,
  onOpen,
}: ChaoUnitProgressCardProps) {
  const pct = metrics?.generalPct ?? 0;
  const photos = metrics?.photoCount ?? 0;
  const last = metrics?.lastUpdate;
  const status = metrics?.status ?? "Sem lançamento de avanço";
  const pendingCount = metrics?.pendingApprovalCount ?? 0;
  const uc = constructiveUnitLabel?.trim();
  const pendingLabel =
    pendingCount === 1 ? "1 aprovação pendente" : `${pendingCount} aprovações pendentes`;

  return (
    <Link
      to={chaoUnitRoute(unit.id)}
      state={{ unitBadge: listUnitBadge(unit.name, unit.id) }}
      onClick={onOpen}
      className={cn(
        "group block rounded-xl border border-slate-200 bg-white p-3 shadow-card",
        "transition-all duration-200 ease-out",
        "hover:-translate-y-0.5 hover:scale-[1.02] hover:shadow-lg hover:border-brand-400/70",
      )}
    >
      <div className="flex items-start justify-between gap-1">
        <div className="min-w-0 flex-1">
          <p className="text-[9px] font-semibold text-slate-400 uppercase tracking-wide truncate">
            {unit.scopeLabel}
          </p>
          <h3 className="text-sm font-bold text-slate-900 mt-0.5 leading-tight truncate group-hover:text-brand-700 transition-colors">
            {unit.name}
          </h3>
          <p className="text-[10px] text-slate-400 font-mono truncate">ID {unit.id}</p>
          {uc ? (
            <p className="text-[9px] text-slate-500 mt-0.5 truncate" title={uc}>
              UC: {uc}
            </p>
          ) : null}
        </div>
        <div className="flex items-start gap-1 flex-shrink-0">
          {pendingCount > 0 ? (
            <span
              className="relative inline-flex items-center justify-center"
              aria-label={pendingLabel}
              title={pendingLabel}
            >
              <AlertCircle className="chao-pending-blink w-4 h-4 text-red-500" aria-hidden />
              <span className="absolute -top-1.5 -right-1.5 min-w-[14px] h-3.5 px-0.5 rounded-full bg-red-600 text-white text-[8px] font-bold leading-[14px] text-center tabular-nums">
                {pendingCount > 99 ? "99+" : pendingCount}
              </span>
            </span>
          ) : null}
          <ChevronRight className="w-4 h-4 text-slate-300 group-hover:text-brand-500 flex-shrink-0" />
        </div>
      </div>

      <div className="mt-2 inline-flex max-w-full items-center px-2 py-0.5 rounded-full bg-slate-100 text-[10px] font-semibold text-slate-700 truncate">
        {status}
      </div>

      <div className="mt-2">
        <div className="flex justify-between text-[10px] font-medium text-slate-500 mb-1">
          <span>Execução</span>
          <span className="tabular-nums text-slate-800">{pct}%</span>
        </div>
        <div className="h-1.5 rounded-full bg-slate-100 overflow-hidden">
          <div
            className={cn(
              "h-full rounded-full transition-all duration-500",
              pct >= 99 ? "bg-emerald-500" : pct >= 50 ? "bg-brand-500" : "bg-amber-400",
            )}
            style={{ width: `${Math.min(100, pct)}%` }}
          />
        </div>
      </div>

      <div className="mt-2.5 grid grid-cols-2 gap-1.5 text-[10px]">
        <div className="rounded-lg bg-slate-50 px-2 py-1.5 border border-slate-100">
          <p className="font-semibold uppercase text-slate-400 text-[9px]">Fotos</p>
          <p className="font-bold text-slate-800 flex items-center gap-1 mt-0.5">
            <ImageIcon className="w-3 h-3 text-slate-400" />
            {photos}
          </p>
        </div>
        <div className="rounded-lg bg-slate-50 px-2 py-1.5 border border-slate-100 min-w-0">
          <p className="font-semibold uppercase text-slate-400 text-[9px]">Atualização</p>
          <p className="font-semibold text-slate-800 mt-0.5 leading-tight truncate">
            {last ? formatDateTime(last) : "—"}
          </p>
        </div>
      </div>

      <div className="mt-2 flex items-center gap-1 text-[10px] font-semibold text-brand-600 opacity-80 group-hover:opacity-100">
        <Camera className="w-3 h-3" />
        Abrir
      </div>
    </Link>
  );
}
